import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export const categories = {
	"PVT": [
		{ "gas_pvt": [] },
		{ "condensate_pvt": [] },
		{ "hydrates": [] },
		{ "water_content": [] },
		{ "import_pvt_table": [] },
		{ "correlation_matching": [] }
	],
	"WELL_PERFORMANCE": [
		{ "ipr": [] },
		{ "vlp": [] },
		{ "gas_rate_critical": [] },
		{ "liquid_loading": [] },
		{ "system_plot": [] },
		{ "whp_ipr": [] },
		{ "choke": [] },
		{ "nozzle_analysis": [] },
		{ "dict_debit": [] },
		{ "dict_analysis": [] },
		{ "water_coning": [] }
	],
	"PIPELINES": [
		{ "pipeline": ["diametr", "pressure_drop", "hydrates", "slugs"] },
		{ "complicated_pipeline": [] },
		{ "well_pipeline": [] }
	],
	"WELL_TESTING": [
		{ "liquid_loading": [] },
		{ "bhp_calculation": [] },
		{ "production_time": [] },
		{ "well_test_design": [] },
		{ "separation": [] },
		{ "dict": [] }
	],
	"RE": [
		{ "material_balance": [] },
		{ "GIIP": [] },
		{ "prod_profile_without_network": [] },
		{ "prod_profile_with_network": [] },
		{ "gtm": [] },
		{ "variable_production_profile_assessment": [] },
		{ "profile_optimization": [] }
	],
	"ECONOMICS": [
		{ "economic_indicators": [] },
		{ "variable_econnomic": [] }
	],
} as const

// export type AppTab = 'pvt' | 'water-content' | 'hydrates' | 'ipr' | 'vlp' | 'liquid-loading' |
// 	'system-plot' | 'system-plot-dict' | 'water-coning' | 'pipeline' | 'bhp-calculation' | 'production-time' |
// 	'well-test-design' | 'separation' | 'dict' | 'material-balance';

// export type AppTab = 'pvt' | 'water-content' | 'hydrates' | 'ipr' | 'vlp' | 'liquid-loading' |
// 	'system-plot' | 'system-plot-dict' | 'water-coning' | 'pipeline' | 'bhp-calculation' | 'production-time' |
// 	'well-test-design' | 'separation' | 'dict' | 'material-balance';

export type AppTab = "PVT" | "WELL_PERFORMANCE" | "PIPELINES" | "WELL_TESTING" | "RE" | "ECONOMICS";

type PVTSubCatId = "gas_pvt" | "condensate_pvt" | "hydrates" | "water_content" | "import_pvt_table" | "correlation_matching";
type WellPerformanceSubCatId = "ipr" | "vlp" | "liquid_loading" | "system_plot" | "whp_ipr" | "choke" | "nozzle_analysis" | "dict_debit" | "dict_analysis" | "water_coning";
type PipelinesSubCatId = "pipeline" | "complicated_pipeline" | "well_pipeline";
type WellTestingSubCatId = "liquid_loading" | "bhp_calculation" | "production_time" | "well_test_design" | "separation" | "dict";
type RESubCatId = "material_balance" | "GIIP" | "prod_profile_without_network" | "prod_profile_with_network" | "gtm" | "variable";
type EconomicsSubCatId = "economic_indicators" | "variable_econnomic";

type SubCatId = PVTSubCatId | WellPerformanceSubCatId | PipelinesSubCatId | WellTestingSubCatId | RESubCatId | EconomicsSubCatId;

type IPRSubSubCatId = "vertical" | "horizontal" | "multilateral" | "multiple_frac";
type VLPSubSubCatId = "average_tz" | "cullender" | "gas_rate_critical" | "gray" | "beggs_brill" | "reserve";
type PipelineSubSubCatId = "diametr" | "pressure_drop" | "hydrates" | "slugs";

export type SubSubCatId = IPRSubSubCatId | VLPSubSubCatId | PipelineSubSubCatId;

interface AppState {
	activeTab: AppTab,
	activeSubCategory: SubCatId | SubSubCatId,
	navigationCollapsed: boolean,
	userSettingsPopupIsOpen: boolean,
	activePage: 'calculator' | 'userProfile',
	authError: boolean,
	secondWellIsOpen: boolean,

	tabs: readonly AppTab[],
	// calculators: readonly AppTab[],
	isLoading: boolean,
	notifications: Notification[],
	calculators: Readonly<typeof categories>
}

interface Notification {
	id: string,
	type: 'success' | 'error' | 'warning' | 'info',
	message: string,
	timestamp: number,
}

const initialState: AppState = {
	activeTab: 'PVT',
	// activeTab: 'WELL_PERFORMANCE',
	activeSubCategory: 'gas_pvt',
	// activeSubCategory: 'vlp', //'gas_pvt',
	navigationCollapsed: false,
	tabs: ['PVT', 'WELL_PERFORMANCE', 'PIPELINES', 'WELL_TESTING', 'RE', 'ECONOMICS'],
	isLoading: false,
	notifications: [],
	calculators: categories,
	userSettingsPopupIsOpen: false,
	activePage: 'calculator',
	authError: false,
	secondWellIsOpen: false,
}

const appSlice = createSlice({
	name: 'app',
	initialState,
	selectors: {
		getCalculators: (state: AppState) => {
			return state.calculators[state.activeTab];
		},
		getUserSettingsPopupIsOpen: (state: AppState) => {
			return state.userSettingsPopupIsOpen;
		},
		getActivePage: (state: AppState) => {
			return state.activePage;
		},
		getAuthError: (state: AppState) => {
			return state.authError;
		},
		getSecondWellIsOpen: (state: AppState) => {
			return state.secondWellIsOpen;
		},
	},
	reducers: {
		setActiveTab: (state, action: PayloadAction<AppTab>) => {
			state.activeTab = action.payload
		},
		setActiveSubCategory: (state, action: PayloadAction<SubCatId | SubSubCatId>) => {
			state.activeSubCategory = action.payload
		},
		setNavigationCollapsed: (state, action: PayloadAction<boolean>) => {
			state.navigationCollapsed = action.payload
		},
		setUserSettingsPopupIsOpen: (state, action: PayloadAction<boolean>) => {
			state.userSettingsPopupIsOpen = action.payload
		},
		setActivePage: (state, action: PayloadAction<'calculator' | 'userProfile'>) => {
			state.activePage = action.payload
		},
		setAuthError: (state, action: PayloadAction<boolean>) => {
			state.authError = action.payload
		},
		setSecondWellIsOpen: (state, action: PayloadAction<boolean>) => {
			state.secondWellIsOpen = action.payload
		},
	},
})

export const {
	setActiveTab,
	setActiveSubCategory,
	setNavigationCollapsed,
	setUserSettingsPopupIsOpen,
	setActivePage,
	setAuthError,
	setSecondWellIsOpen
} = appSlice.actions

export const {
	getCalculators,
	getUserSettingsPopupIsOpen,
	getActivePage,
	getAuthError,
	getSecondWellIsOpen
} = appSlice.selectors

export default appSlice